import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useShare } from '../hooks/useShare';
import { useCourses } from '../hooks/useCourses';
import { useCart } from '../contexts/CartContext';
import { useCourseProgress } from '../hooks/useCourseProgress';
import { formatGhanaCedis } from '../utils/formatUtils';
import { renderHTMLContent, getCleanText } from '../utils/htmlRenderer';
import { 
  AcademicCapIcon, 
  ChartBarIcon,
  ClockIcon,
  ArrowRightIcon,
  PlayIcon,
  PlusIcon,
  UserIcon,
  ClipboardDocumentIcon,
  LinkIcon,
  BuildingOfficeIcon,
  MegaphoneIcon,
  MagnifyingGlassIcon,
  BookOpenIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  SparklesIcon,
  TrophyIcon,
  StarIcon,
  FireIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import CourseCard from '../components/CourseCard';
import CatalogCourseCard from '../components/CatalogCourseCard';
import CourseDetailsModal from '../components/CourseDetailsModal';
import EditCourseModal from '../components/EditCourseModal';
import AIChatButton from '../components/AIChatButton';

const StudentDashboard: React.FC = () => {
  const { currentUser } = useAuth();
  const { copyToClipboard: shareCopyToClipboard } = useShare();
  const { 
    courses, 
    loading: coursesLoading, 
    fetchAuthorCourses,
    fetchAllCourses
  } = useCourses();
  const { addToCart } = useCart();
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('ICAG');
  const [enrolledCourses, setEnrolledCourses] = useState<any[]>([]);
  const [enrolledCoursesLoading, setEnrolledCoursesLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const [coursesPerPage] = useState(4);
  const navigate = useNavigate();
  const [selectedCourse, setSelectedCourse] = useState<any | null>(null);
  const [showCourseModal, setShowCourseModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState<any | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Determine user role and view type
  const normalizedRole = (currentUser?.role || '').toString().trim().toLowerCase();
  const isAdmin = normalizedRole === 'admin';
  const isAuthor = normalizedRole === 'author'; // Allow authors regardless of status to see their courses
  const isStudent = normalizedRole === 'student' || (!isAdmin && !isAuthor);

  // Preload critical images for better LCP
  useEffect(() => {
    if (currentUser?.avatar) {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = currentUser.avatar;
      // Set fetchPriority using setAttribute for better compatibility
      link.setAttribute('fetchpriority', 'high');
      document.head.appendChild(link);
      
      return () => {
        if (document.head.contains(link)) {
          document.head.removeChild(link);
        }
      };
    }
  }, [currentUser?.avatar]);

  // Track large screen to disable carousel and show all three at once on desktop
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mediaQueryList = window.matchMedia('(min-width: 1024px)');
    const handleChange = (event: any) => setIsLargeScreen(!!event.matches);
    setIsLargeScreen(mediaQueryList.matches);
    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', handleChange);
      return () => mediaQueryList.removeEventListener('change', handleChange);
    } else if (typeof (mediaQueryList as any).addListener === 'function') {
      (mediaQueryList as any).addListener(handleChange);
      return () => (mediaQueryList as any).removeListener(handleChange);
    }
  }, []);

  // Track if we've already fetched courses to prevent unnecessary re-fetches
  const hasFetchedCourses = useRef(false);
  const previousProgram = useRef<string | undefined>(undefined);
  const hasFetchedEnrolledCourses = useRef(false); // Track if enrolled courses have been fetched





  // Helper function to copy text to clipboard
  const copyToClipboard = async (text: string, field: string) => {
    const success = await shareCopyToClipboard(text, `${field === 'code' ? 'Referral code' : 'Affiliate link'} copied to clipboard!`);
    if (success) {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    }
  };

  // Generate affiliate link
  const generateAffiliateLink = () => {
    if (!currentUser?.referralCode) return '';
    return `${window.location.origin}/affiliate?ref=${currentUser.referralCode}`;
  };

  // Handle search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/app/catalog?search=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  // Handle add to cart
  const handleAddToCart = (course: any) => {
    addToCart({
      type: 'course',
      itemId: course.id,
      name: course.name,
      price: parseFloat(course.pricee) || 0,
      image: course.image_url
    });
  };

  // Handle edit course
  const handleEditCourse = useCallback((course: any) => {
    setEditingCourse(course);
    setShowEditModal(true);
  }, []);

  // Handle edit modal close
  const handleEditModalClose = useCallback(() => {
    setShowEditModal(false);
    setEditingCourse(null);
  }, []);



  // Fetch user's enrolled courses from Firestore
  const fetchEnrolledCourses = useCallback(async () => {
    if (!currentUser?.uid) return;
    
    // Only fetch once per session, then rely on cache
    if (hasFetchedEnrolledCourses.current) {
      console.log('[Dashboard] Enrolled courses already fetched, using cache');
      return;
    }
    
    const CACHE_KEY = `enrolled_courses_${currentUser.uid}`;
    const TTL_MS = 10 * 60 * 1000; // 10 minutes cache
    let hasFreshCache = false;

    // Try cache first
    try {
      const cachedRaw = localStorage.getItem(CACHE_KEY);
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw) as { data: any[]; updatedAt: number };
        if (Array.isArray(cached.data)) {
            setEnrolledCourses(cached.data);
          if (cached.updatedAt && Date.now() - cached.updatedAt < TTL_MS) {
            hasFreshCache = true;
            console.log('[Dashboard] Using fresh cache for enrolled courses');
          } else {
            console.log('[Dashboard] Cache expired, will refresh in background');
          }
        }
      }
    } catch (error) {
      console.warn('[Dashboard] Cache read error:', error);
    }

    // If no fresh cache, show loader while fetching
    if (!hasFreshCache) {
    setEnrolledCoursesLoading(true);
    }

    try {
      // Import Firestore functions
      const { collection, doc, getDocs, getDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      
      // Fetch from users > currentUserId > Courses subcollection
      const userCoursesRef = collection(db, 'users', currentUser.uid, 'Courses');
      const querySnapshot = await getDocs(userCoursesRef);
      
      const enrolled = [] as any[];
      
      for (const docSnapshot of querySnapshot.docs) {
        const courseData = docSnapshot.data();
        const courseId = courseData.course_id;
        // Avoid heavy classroom fetches here to reduce reads on refresh
        enrolled.push({
          id: docSnapshot.id,
          course_id: courseId,
          enrolled_at: courseData.enrolled_at,
          expire_date: courseData.expire_date,
          status: courseData.status,
          classrooms: null
        });
      }
      
      setEnrolledCourses(enrolled);
      
      // Mark as fetched for this session
      hasFetchedEnrolledCourses.current = true;
      
      // Update cache with longer TTL for session persistence
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ 
          data: enrolled, 
          updatedAt: Date.now() 
        }));
        console.log('[Dashboard] Enrolled courses cached successfully');
      } catch (error) {
        console.warn('[Dashboard] Cache write error:', error);
      }
      
    } catch (error) {
      console.error('Error fetching enrolled courses from Firestore:', error);
    } finally {
      setEnrolledCoursesLoading(false);
    }
  }, [currentUser?.uid]);

  // Function to manually refresh enrolled courses (for when user enrolls in new course)
  const refreshEnrolledCourses = useCallback(async () => {
    if (!currentUser?.uid) return;
    
    console.log('[Dashboard] Manually refreshing enrolled courses');
    hasFetchedEnrolledCourses.current = false; // Reset flag
    setEnrolledCoursesLoading(true);
    
    try {
      // Import Firestore functions
      const { collection, getDocs } = await import('firebase/firestore');
      const { db } = await import('../firebase/config');
      
      const userCoursesRef = collection(db, 'users', currentUser.uid, 'Courses');
      const querySnapshot = await getDocs(userCoursesRef);
      
      const enrolled = [] as any[];
      
      for (const docSnapshot of querySnapshot.docs) {
        const courseData = docSnapshot.data();
        const courseId = courseData.course_id;
        enrolled.push({
          id: docSnapshot.id,
          course_id: courseId,
          enrolled_at: courseData.enrolled_at,
          expire_date: courseData.expire_date,
          status: courseData.status,
          classrooms: null
        });
      }
      
      setEnrolledCourses(enrolled);
      hasFetchedEnrolledCourses.current = true;
      
      // Update cache
      const CACHE_KEY = `enrolled_courses_${currentUser.uid}`;
      try {
        localStorage.setItem(CACHE_KEY, JSON.stringify({ 
          data: enrolled, 
          updatedAt: Date.now() 
        }));
      } catch (error) {
        console.warn('[Dashboard] Cache write error:', error);
      }
      
    } catch (error) {
      console.error('Error refreshing enrolled courses:', error);
    } finally {
      setEnrolledCoursesLoading(false);
    }
  }, [currentUser?.uid]);

  // Function to clear the enrolled courses cache
  const clearEnrolledCoursesCache = useCallback(() => {
    if (!currentUser?.uid) return;
    
    console.log('[Dashboard] Clearing enrolled courses cache');
    hasFetchedEnrolledCourses.current = false;
    
    const CACHE_KEY = `enrolled_courses_${currentUser.uid}`;
    try {
      localStorage.removeItem(CACHE_KEY);
      console.log('[Dashboard] Cache cleared successfully');
    } catch (error) {
      console.warn('[Dashboard] Cache clear error:', error);
    }
  }, [currentUser?.uid]);

  // Get courses for the active category
  const getActiveCategoryCourses = useCallback(() => {
    if (!courses || courses.length === 0) return [];
    
    const filteredCourses = courses.filter(course => {
      // Don't display draft courses
      if (course.status === "draft") {
        return false;
      }
      
      // Check if course matches the active category
      if (activeCategory === 'ICAG' || activeCategory === 'CITG') {
        // For ICAG and CITG, check if the course program matches
        if (!course.program) {
          return false;
        }
        
        let matches = false;
        if (Array.isArray(course.program)) {
          matches = course.program.includes(activeCategory);
        } else {
          matches = course.program === activeCategory;
        }
        
        return matches;
      } else if (activeCategory === 'Intervention') {
        // For Intervention courses
        const isIntervention = course.cat_id?.toLowerCase().includes('intervention') || 
                              course.cat_id?.toLowerCase().includes('remedial') ||
                              course.cat_id?.toLowerCase().includes('support');
        return isIntervention;
      } else if (activeCategory === 'Masterclass') {
        // For Masterclass courses
        const isMasterclass = course.cat_id?.toLowerCase().includes('masterclass') || 
                             course.cat_id?.toLowerCase().includes('expert') ||
                             course.cat_id?.toLowerCase().includes('intensive');
        return isMasterclass;
      } else if (activeCategory === 'On Demand') {
        // For On Demand courses
        const isOnDemand = course.cat_id?.toLowerCase().includes('on-demand') || 
                          course.cat_id?.toLowerCase().includes('self-paced') ||
                          course.cat_id?.toLowerCase().includes('flexible');
        return isOnDemand;
      } else if (activeCategory === 'Profstudy Plus') {
        // For Profstudy Plus courses
        const isProfstudyPlus = course.cat_id?.toLowerCase().includes('profstudy-plus') || 
                               course.cat_id?.toLowerCase().includes('premium') ||
                               course.cat_id?.toLowerCase().includes('plus');
        return isProfstudyPlus;
      } else if (activeCategory === 'Corporate Training') {
        // For Corporate Training, check if it's a corporate course
        const isCorporate = course.cat_id?.toLowerCase().includes('corporate') || 
                           course.cat_id?.toLowerCase().includes('training') ||
                           course.cat_id?.toLowerCase().includes('leadership');
        return isCorporate;
      } else if (activeCategory === 'Digital Marketing') {
        // For Digital Marketing, check if it's a marketing course
        const isMarketing = course.cat_id?.toLowerCase().includes('marketing') || 
                           course.cat_id?.toLowerCase().includes('digital') ||
                           course.cat_id?.toLowerCase().includes('social');
        return isMarketing;
      }
      return false;
    });
    
    return filteredCourses;
  }, [courses, activeCategory]);

  // Get paginated courses for the active category
  const getPaginatedCategoryCourses = useCallback(() => {
    const allCourses = getActiveCategoryCourses();
    const startIndex = (currentPage - 1) * coursesPerPage;
    const endIndex = startIndex + coursesPerPage;
    return allCourses.slice(startIndex, endIndex);
  }, [getActiveCategoryCourses, currentPage, coursesPerPage]);

  // Reset to first page when category changes
  useEffect(() => {
    setCurrentPage(1);
  }, [activeCategory]);

    // Get user's courses based on role (for the "Your Courses" section)
  const getUserCourses = useCallback(() => {
    if (!courses || courses.length === 0) return [];
    
    if (isAdmin) {
      // Admin sees all courses
      return courses.slice(0, 3); // Show top 3 courses for admin
    } else if (isAuthor) {
      // Author sees their created courses - check both userId and author_id fields
      const authorCourses = courses.filter(course => 
        course.userId === currentUser?.uid || course.author_id === currentUser?.uid
      );
      return authorCourses.slice(0, 3); // Show top 3 author courses
    } else {
      // Students see their enrolled courses
      if (!enrolledCourses.length) return [];
      
      // Map enrolled course IDs to full course data and remove duplicates
      const userCoursesMap = new Map();
      
      enrolledCourses.forEach(enrolledCourse => {
        const course = courses.find(c => c.id === enrolledCourse.course_id);
        if (course && !userCoursesMap.has(course.id)) {
          // Calculate expiry date: if not provided, use 3 months from enrollment
          let expireDate = enrolledCourse.expire_date;
          if (!expireDate && enrolledCourse.enrolled_at) {
            try {
              const enrolledDate = new Date(enrolledCourse.enrolled_at);
              
              // Check if the date is valid
              if (isNaN(enrolledDate.getTime())) {
                console.warn(`⚠️ Invalid enrolled_at date: ${enrolledCourse.enrolled_at}`);
                // Use current date as fallback
                const currentDate = new Date();
                const threeMonthsLater = new Date(currentDate);
                threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
                expireDate = threeMonthsLater.toISOString();

              } else {
                const threeMonthsLater = new Date(enrolledDate);
                threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
                expireDate = threeMonthsLater.toISOString();

              }
            } catch (error) {
              console.error(`❌ Error calculating expiry date:`, error);
              // Use current date as fallback
              const currentDate = new Date();
              const threeMonthsLater = new Date(currentDate);
              threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
              expireDate = threeMonthsLater.toISOString();

            }
          }
          
          userCoursesMap.set(course.id, {
            ...course,
            enrolled_at: enrolledCourse.enrolled_at,
            expire_date: expireDate,
            status: enrolledCourse.status,
            classrooms: enrolledCourse.classrooms
          });
        }
      });
      
      let userCourses = Array.from(userCoursesMap.values());
      
      // Apply search filter if query exists
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        userCourses = userCourses.filter(course => 
          course.name?.toLowerCase().includes(query) ||
          course.program?.toString().toLowerCase().includes(query) ||
          course.cat_id?.toLowerCase().includes(query) ||
          course.description?.toLowerCase().includes(query)
        );
      }
      
      return userCourses.slice(0, 3); // Show only top 3 enrolled courses
    }
  }, [courses, enrolledCourses, searchQuery, isAdmin, isAuthor, currentUser?.uid]);

  // Carousel functions
  const nextSlide = useCallback(() => {
    const userCourses = getUserCourses().filter((course): course is NonNullable<typeof course> => course !== null);
    if (userCourses.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % userCourses.length);
    }
  }, [getUserCourses]);

  const prevSlide = useCallback(() => {
    const userCourses = getUserCourses().filter((course): course is NonNullable<typeof course> => course !== null);
    if (userCourses.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + userCourses.length) % userCourses.length);
    }
  }, [getUserCourses]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  // Auto-scroll effect - only when there are more than 2 items
  useEffect(() => {
    if (!isAutoScrolling) return;
    
    const userCourses = getUserCourses().filter((course): course is NonNullable<typeof course> => course !== null);
    // Don't auto-scroll if there are 2 or fewer items
    if (userCourses.length <= 2) return;

    const interval = setInterval(() => {
      nextSlide();
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [isAutoScrolling, nextSlide, getUserCourses]);

  // Pause auto-scroll on hover - only when auto-scrolling is actually active
  const handleCarouselHover = useCallback(() => {
    const userCourses = getUserCourses().filter((course): course is NonNullable<typeof course> => course !== null);
    // Only pause auto-scroll if there are more than 2 items
    if (userCourses.length > 2) {
      setIsAutoScrolling(false);
    }
  }, [getUserCourses]);

  const handleCarouselLeave = useCallback(() => {
    const userCourses = getUserCourses().filter((course): course is NonNullable<typeof course> => course !== null);
    // Only resume auto-scroll if there are more than 2 items
    if (userCourses.length > 2) {
      setIsAutoScrolling(true);
    }
  }, [getUserCourses]);

  // Reset currentSlide to 0 when there are 2 or fewer courses (no need for carousel)
  useEffect(() => {
    const userCourses = getUserCourses().filter((course): course is NonNullable<typeof course> => course !== null);
    if (userCourses.length <= 2 && currentSlide !== 0) {
      setCurrentSlide(0);
    }
  }, [getUserCourses, currentSlide]);

  // Pagination functions
  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const nextPage = useCallback(() => {
    const totalPages = Math.ceil(getActiveCategoryCourses().length / coursesPerPage);
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  }, [currentPage, getActiveCategoryCourses, coursesPerPage]);

  const prevPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);







  // Set default active category based on user's program
  useEffect(() => {
    if (!hasFetchedCourses.current) {
      previousProgram.current = currentUser?.program || undefined;
      hasFetchedCourses.current = true;
    }
  }, [currentUser?.program]);

  // Handle program changes and set active category
  useEffect(() => {
    if (currentUser?.program && (currentUser.program === 'ICAG' || currentUser.program === 'CITG')) {
      setActiveCategory(currentUser.program);
    }
  }, [currentUser?.program]);

  // Handle program changes (no need to refetch courses for dashboard)
  useEffect(() => {
    // Update previous program reference but don't refetch courses
    // Dashboard should always show all courses for all categories
    if (currentUser?.program !== previousProgram.current) {
      previousProgram.current = currentUser?.program || undefined;
    }
  }, [currentUser?.program]);

  // Course categories
  const courseCategories = useMemo(() => [
    {
      name: 'ICAG',
      description: 'Institute of Chartered Accountants Ghana',
      icon: BuildingOfficeIcon,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-700',
      href: '/app/catalog?category=icag'
    },
    {
      name: 'CITG',
      description: 'Chartered Institute of Taxation Ghana',
      icon: ChartBarIcon,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-700',
      href: '/app/catalog?category=citg'
    },
    {
      name: 'Corporate Training',
      description: 'Professional development programs',
      icon: UserIcon,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-700',
      href: '/app/catalog?category=corporate'
    },
    {
      name: 'Digital Marketing',
      description: 'Modern marketing strategies',
      icon: MegaphoneIcon,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-700',
      href: '/app/catalog?category=digital-marketing'
    },
    {
      name: 'Intervention',
      description: 'Targeted learning interventions',
      icon: AcademicCapIcon,
      color: 'from-red-500 to-red-600',
      bgColor: 'bg-red-50',
      textColor: 'text-red-700',
      href: '/app/catalog?category=intervention'
    },
    {
      name: 'Masterclass',
      description: 'Expert-led intensive sessions',
      icon: UserIcon,
      color: 'from-indigo-500 to-indigo-600',
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-700',
      href: '/app/catalog?category=masterclass'
    },
    {
      name: 'On Demand',
      description: 'Self-paced learning modules',
      icon: ClockIcon,
      color: 'from-teal-500 to-teal-600',
      bgColor: 'bg-teal-50',
      textColor: 'text-teal-700',
      href: '/app/catalog?category=on-demand'
    },
    {
      name: 'Profstudy Plus',
      description: 'Premium learning experience',
      icon: AcademicCapIcon,
      color: 'from-amber-500 to-amber-600',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-700',
      href: '/app/catalog?category=profstudy-plus'
    }
  ], []);

  // Fetch enrolled courses when user is available
  useEffect(() => {
    if (currentUser?.uid) {
      fetchEnrolledCourses();
    }
  }, [currentUser?.uid, fetchEnrolledCourses]);

  // Fetch courses based on user role
  useEffect(() => {
    if (!currentUser) return;

    if (isAdmin) {
      // Admin sees all courses
      fetchAllCourses();
    } else if (isAuthor) {
      // Author sees their created courses
      fetchAuthorCourses(currentUser.uid);
    }
  }, [currentUser, isAdmin, isAuthor, fetchAllCourses, fetchAuthorCourses]);



  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 overflow-x-hidden">
      {/* Hero Section */}
      <section className="hero-section relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 overflow-hidden">
        {/* Main Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12" style={{ width: '100%', maxWidth: '100vw' }}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-8 items-center" style={{ width: '100%' }}>
            
            {/* Left Side - Text Content */}
            <div className="text-center lg:text-left space-y-3 sm:space-y-4" style={{ width: '100%', maxWidth: '100%', overflow: 'hidden' }}>
              {/* Welcome Badge */}
              <div className="inline-flex items-center px-3 py-1.5 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-white text-xs font-medium">
                <SparklesIcon className="h-3 w-3 mr-1.5 text-yellow-300" />
                Welcome back, {currentUser?.name || 'Student'}! ✨
        </div>


        
        {/* Search Bar */}
              <form onSubmit={handleSearch} className="relative max-w-sm mx-auto lg:mx-0" style={{ width: '100%', maxWidth: '100%' }}>
          <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-blue-300" />
            <input
              type="text"
              placeholder="Search for courses, topics, or skills..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-9 pr-16 py-2 bg-white/90 backdrop-blur-sm border-0 rounded-lg text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white transition-all duration-200 text-sm"
            />
            <button
              type="submit"
                    className="absolute right-1.5 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-2.5 py-1.5 rounded-md text-xs font-medium transition-all duration-200"
            >
              Search
            </button>
          </div>
        </form>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-2 py-3" style={{ width: '100%', maxWidth: '100%' }}>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {enrolledCourses.length || 0}
                  </div>
                  <div className="text-xs text-blue-200">Active Courses</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {currentUser?.totalrefs || 0}
                  </div>
                  <div className="text-xs text-blue-200">Referrals</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-xl font-bold text-white mb-0.5">
                    {formatGhanaCedis(currentUser?.earnings || 0)}
                  </div>
                  <div className="text-xs text-blue-200">Earnings</div>
                </div>
      </div>

              {/* Call-to-Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center lg:justify-start" style={{ width: '100%', maxWidth: '100%' }}>
                <Link
                  to="/app/catalog"
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-lg text-sm"
                >
                  <RocketLaunchIcon className="h-4 w-4 mr-1.5" />
                  Explore Courses
                </Link>
                <Link
                  to="/app/ai-learning"
                  className="inline-flex items-center justify-center px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold rounded-lg hover:bg-white/20 transition-all duration-200 text-sm"
                >
                  <SparklesIcon className="h-4 w-4 mr-1.5" />
                  AI Learning
                </Link>
              </div>
            </div>

            {/* Right Side - Visual Elements - Hidden on mobile for stability */}
            <div className="relative hidden xl:block">
              {/* Achievement Cards */}
              <div className="space-y-4">
                {/* Top Achievement Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                      <TrophyIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Course Progress</h3>
                      <p className="text-blue-200 text-sm">Keep up the great work!</p>
                    </div>
                  </div>
                </div>

                {/* Bottom Achievement Card */}
                <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-6 ml-8">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center">
                      <StarIcon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-white font-semibold">Learning Streak</h3>
                      <p className="text-blue-200 text-sm">Consistency is key!</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Wave Divider */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="w-full h-8 sm:h-12 text-gray-50" viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" opacity=".25" fill="currentColor"></path>
            <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" opacity=".5" fill="currentColor"></path>
            <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill="currentColor"></path>
          </svg>
        </div>
      </section>

      {/* Main Dashboard Content */}
      <div className="max-w-7xl mx-auto px-0 sm:px-6 lg:px-8 pt-0 sm:pt-6 relative z-20 space-y-4 sm:space-y-8">
      {/* Your Courses Section */}
        <div className="bg-white rounded-none sm:rounded-2xl shadow-xl border-0 sm:border border-gray-200 p-3 sm:p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center space-x-2">
            <h2 className="text-lg lg:text-xl font-bold text-gray-900">
              {isAdmin ? 'All Courses' : isAuthor ? 'Your Courses' : 'Your Courses'}
            </h2>
              <div className="group relative">
                <svg className="w-4 h-4 text-gray-400 cursor-help" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
                </svg>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  {isAdmin ? 'View and manage all courses in the system' : isAuthor ? 'View and manage your created courses' : 'Data is cached to reduce loading time. Use the refresh button to get latest data.'}
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                </div>
              </div>
            </div>
            <p className="text-xs lg:text-sm text-gray-600 mt-1">
              {isAdmin ? 'Monitor course performance and manage all content' : isAuthor ? 'Edit your courses and track student engagement' : 'Continue where you left off'}
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <Link
              to="/app/courses"
              className="text-primary-600 hover:text-primary-700 text-xs lg:text-sm font-medium flex items-center"
            >
              View All
              <ArrowRightIcon className="h-3 w-3 lg:h-4 lg:w-4 ml-1" />
            </Link>
          </div>
        </div>
        
        <div 
          className="relative"
          onMouseEnter={handleCarouselHover}
          onMouseLeave={handleCarouselLeave}
        >
          {(coursesLoading || (enrolledCoursesLoading && !isAdmin && !isAuthor)) ? (
            // Loading state - horizontal layout skeleton
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 animate-pulse flex items-center space-x-4">
                  <div className="bg-gray-200 rounded-lg w-32 h-32 flex-shrink-0"></div>
                  <div className="flex-1 space-y-2">
                    <div className="bg-gray-200 h-4 rounded w-3/4"></div>
                    <div className="bg-gray-200 h-3 rounded w-1/2"></div>
                    <div className="bg-gray-200 h-3 rounded w-2/3"></div>
                    <div className="bg-gray-200 h-8 rounded w-full"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (() => {
            const userCoursesList = getUserCourses().filter((course): course is NonNullable<typeof course> => course !== null);
            const isCarousel = userCoursesList.length > 2 && !isLargeScreen;
            const slideWidthClass = isCarousel
              ? 'w-full'
              : (isLargeScreen ? 'w-full lg:w-1/3' : (userCoursesList.length === 2 ? 'w-1/2' : 'w-full'));

            return userCoursesList.length > 0 ? (
            // User's enrolled courses - carousel layout for 3+ items, static layout for 1-2 items
            <div className={`relative ${isCarousel ? 'overflow-hidden' : ''}`}>
              <div
                className={`flex ${isCarousel ? 'gap-0' : 'gap-2'} transition-transform duration-500 ease-in-out ${isCarousel ? '' : 'w-full'}`}
                style={{ transform: isCarousel ? `translateX(-${currentSlide * 100}%)` : 'translateX(0%)' }}
              >
                {userCoursesList.map((course) => (
                  <div key={course.id} className={`${slideWidthClass} flex-shrink-0`}>
                      <CourseCard 
                        course={course} 
                        showProgress={!isAdmin && !isAuthor} // Only show progress for students
                        showEditButton={isAdmin || isAuthor} // Show edit button for admin/author
                        onClick={() => { setSelectedCourse(course); setShowCourseModal(true); }}
                        onEdit={() => handleEditCourse(course)}
                      />
                    </div>
                  ))}
              </div>
              
              {/* Navigation Arrows - only show when there are more than 2 items */}
              {isCarousel && (
                <>
                  <button
                    onClick={prevSlide}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg border border-gray-200 transition-all duration-200 hover:scale-110 z-10"
                    aria-label="Previous course"
                  >
                    <ChevronLeftIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={nextSlide}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 p-2 rounded-full shadow-lg border border-gray-200 transition-all duration-200 hover:scale-110 z-10"
                    aria-label="Next course"
                  >
                    <ChevronRightIcon className="h-5 w-5" />
                  </button>
                </>
              )}
              
              {/* Dots Indicator - only show when there are more than 2 items */}
              {isCarousel && (
                <div className="flex justify-center mt-4 space-x-2">
                  {userCoursesList.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`w-2 h-2 rounded-full transition-all duration-200 ${
                          index === currentSlide 
                            ? 'bg-primary-600 w-6' 
                            : 'bg-gray-300 hover:bg-gray-400'
                        }`}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                </div>
              )}
            </div>
          ) : searchQuery.trim() ? (
            // No search results
            <div className="text-center py-8">
              <MagnifyingGlassIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No courses found
              </h3>
              <p className="text-gray-600 mb-4">
                No courses match your search "{searchQuery}". Try adjusting your search terms.
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                Clear search
              </button>
            </div>
          ) : (
            // No courses state - Simplified
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AcademicCapIcon className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {isAdmin ? 'No courses found' : isAuthor ? 'View or create your courses or manage' : 'No courses yet'}
              </h3>
              <p className="text-gray-600 mb-4">
                {isAdmin 
                  ? 'There are no courses in the system yet.' 
                  : isAuthor 
                  ? 'Access the admin panel to view, create, or manage your courses.'
                  : 'Start your learning journey today'
                }
              </p>
              <Link
                to={isAuthor ? "/app/admin" : "/app/catalog"}
                className="inline-flex items-center px-5 py-2 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                {isAdmin ? 'Browse All Courses' : isAuthor ? 'Create Course' : 'Browse Courses'}
              </Link>
            </div>
          )
          })()}
        </div>
      </div>


            {/* Course Categories Section - Tabbed Interface */}
        <div className="bg-white rounded-none sm:rounded-2xl shadow-xl border-0 sm:border border-gray-200 p-3 sm:p-6 lg:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
          <h2 className="text-lg lg:text-xl font-bold text-gray-900">Course Categories</h2>
          <p className="text-xs lg:text-sm text-gray-600 mt-1">Explore different learning paths</p>
          </div>
          
          {/* View All Link - positioned like in the image */}
          <Link
            to="/app/catalog"
            className="text-primary-600 hover:text-primary-700 text-sm font-medium flex items-center"
          >
            View All
            <ArrowRightIcon className="h-3 w-3 ml-1" />
          </Link>
        </div>
        
        {/* Category Tabs - Sticky when scrolling */}
        <div className="sticky top-16 z-10 bg-white border-b border-gray-200">
          <div className="overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] py-2">
            <div className="flex gap-2 border-b border-gray-200 min-w-max pb-1 px-2 sm:px-3 lg:px-6 max-w-full">
            {courseCategories.map((category) => (
                <button
                key={category.name}
                  onClick={() => setActiveCategory(category.name)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-t-lg font-medium text-sm transition-all duration-200 whitespace-nowrap ${
                    activeCategory === category.name
                      ? 'bg-primary-600 text-white border-b-2 border-primary-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-800'
                  }`}
                >
                  <category.icon className={`h-4 w-4 ${
                    activeCategory === category.name ? 'text-white' : 'text-gray-500'
                  }`} />
                  <span>{category.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
                
        {/* Active Category Content */}
        <div className="min-h-[400px] pt-4">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {courseCategories.find(cat => cat.name === activeCategory)?.name} Courses
                </h3>
            <p className="text-sm text-gray-600">
              {courseCategories.find(cat => cat.name === activeCategory)?.description}
            </p>
          </div>
          
          {/* Courses Grid */}
          {coursesLoading ? (
            // Loading state
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-full">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 animate-pulse flex items-center space-x-4">
                  <div className="bg-gray-200 rounded-lg w-32 h-32 flex-shrink-0"></div>
                  <div className="flex-1 space-y-2">
                    <div className="bg-gray-200 h-4 rounded w-3/4"></div>
                    <div className="bg-gray-200 h-3 rounded w-1/2"></div>
                    <div className="bg-gray-200 h-3 rounded w-2/3"></div>
                    <div className="bg-gray-200 h-8 rounded w-full"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : getActiveCategoryCourses().length > 0 ? (
            <>
              {/* Real courses - matching the image style */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-full">
                {getPaginatedCategoryCourses().map((course) => (
                  <CatalogCourseCard
                    key={`course-category-${course.id}`}
                    course={course}
                    onClick={() => { setSelectedCourse(course); setShowCourseModal(true); }}
                    onAddToCart={handleAddToCart}
                    showEditButton={isAdmin || (isAuthor && course.userId === currentUser?.uid)}
                    onEdit={() => handleEditCourse(course)}
                  />
                ))}
              </div>

              {/* Pagination Controls */}
              {getActiveCategoryCourses().length > coursesPerPage && (
                <div className="flex items-center justify-center mt-6 space-x-2 max-w-full">
                  {/* Previous Page Button */}
                  <button
                    onClick={prevPage}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === 1
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
                    }`}
                    aria-label="Previous page"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  {/* Page Numbers */}
                  {(() => {
                    const totalPages = Math.ceil(getActiveCategoryCourses().length / coursesPerPage);
                    const maxVisiblePages = 5;
                    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                    
                    if (endPage - startPage + 1 < maxVisiblePages) {
                      startPage = Math.max(1, endPage - maxVisiblePages + 1);
                    }

                      const pageNumbers = [];
                    for (let i = startPage; i <= endPage; i++) {
                        pageNumbers.push(i);
                    }

                      return pageNumbers.map(page => (
                      <button
                        key={page}
                        onClick={() => goToPage(page)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          page === currentPage
                            ? 'bg-primary-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
                        }`}
                        aria-label={`Go to page ${page}`}
                      >
                        {page}
                      </button>
                    ));
                  })()}

                  {/* Next Page Button */}
                  <button
                    onClick={nextPage}
                    disabled={currentPage === Math.ceil(getActiveCategoryCourses().length / coursesPerPage)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === Math.ceil(getActiveCategoryCourses().length / coursesPerPage)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200 hover:border-gray-300'
                    }`}
                    aria-label="Next page"
                  >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Page Info */}
              <div className="text-center mt-4 text-sm text-gray-600">
                Showing {((currentPage - 1) * coursesPerPage) + 1} to {Math.min(currentPage * coursesPerPage, getActiveCategoryCourses().length)} of {getActiveCategoryCourses().length} courses
              </div>
            </>
          ) : (
            // No courses found
            <div className="text-center py-12">
              <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No courses found</h3>
              <p className="text-gray-500 mb-4">
                No courses available in the {activeCategory} category at the moment.
              </p>
              <Link
                to="/app/catalog"
                className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Browse All Courses
              </Link>
            </div>
          )}
          
          {/* View All Button */}
          {getActiveCategoryCourses().length > 0 && (
            <div className="text-center mt-6">
              <Link
                to={`/app/catalog?category=${activeCategory.toLowerCase()}`}
                className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                View All {activeCategory} Courses
                <ArrowRightIcon className="h-4 w-4 ml-2" />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Affiliate Program Section */}
         <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-0 sm:border border-green-200 rounded-none sm:rounded-xl p-3 sm:p-4 lg:p-6 max-w-full">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 lg:w-12 lg:h-12 bg-green-100 rounded-lg flex items-center justify-center mr-3 lg:mr-4 flex-shrink-0">
                <svg className="h-5 w-5 lg:h-6 lg:w-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-base lg:text-lg font-semibold text-green-800">Referral Program</h3>
                <p className="text-xs lg:text-sm text-green-600">Earn money by referring friends and colleagues</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 lg:gap-4 mb-4">
              <div className="bg-white/60 rounded-lg p-3 border border-green-200">
                <p className="text-xs lg:text-sm font-medium text-green-700">Total Referrals</p>
                <p className="text-xl lg:text-2xl font-bold text-green-800">{currentUser?.totalrefs || 0}</p>
              </div>
              <div className="bg-white/60 rounded-lg p-3 border border-green-200">
                <p className="text-xs lg:text-sm font-medium text-green-700">Total Earnings</p>
                <p className="text-xl lg:text-2xl font-bold text-green-800">{formatGhanaCedis(currentUser?.earnings || 0)}</p>
              </div>
              <div className="bg-white/60 rounded-lg p-3 border border-green-200">
                <p className="text-xs lg:text-sm font-medium text-green-700">Referral Code</p>
                <div className="flex items-center justify-between">
                  <p className="text-sm lg:text-lg font-mono font-bold text-green-800 truncate">{currentUser?.referralCode || 'Not generated'}</p>
                  {currentUser?.referralCode && (
                    <button
                      onClick={() => copyToClipboard(currentUser.referralCode!, 'code')}
                      className={`p-1 rounded transition-colors ${
                        copiedField === 'code' 
                          ? 'bg-green-500 text-white' 
                          : 'text-green-600 hover:bg-green-100'
                      }`}
                      title="Copy referral code"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Affiliate Link Section */}
            {currentUser?.referralCode && (
              <div className="bg-white/60 rounded-lg p-3 lg:p-4 border border-green-200 mb-4">
                <p className="text-xs lg:text-sm font-medium text-green-700 mb-2">Your Affiliate Link</p>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                  <input
                    type="text"
                    value={generateAffiliateLink()}
                    readOnly
                    className="flex-1 px-3 py-2 bg-white border border-green-200 rounded-lg text-xs lg:text-sm font-mono text-green-800"
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={() => copyToClipboard(generateAffiliateLink(), 'link')}
                      className={`flex-1 sm:flex-none p-2 rounded-lg transition-colors ${
                        copiedField === 'link' 
                          ? 'bg-green-500 text-white' 
                          : 'bg-green-100 text-green-600 hover:bg-green-200'
                      }`}
                      title="Copy affiliate link"
                    >
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => copyToClipboard(generateAffiliateLink(), 'link')}
                      className="flex-1 sm:flex-none p-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      title="Share affiliate link"
                    >
                      <LinkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/affiliate"
                className="inline-flex items-center justify-center px-3 py-2 lg:px-4 lg:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm lg:text-base"
              >
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                View Affiliate Page
              </Link>
              {currentUser?.earnings && currentUser.earnings > 0 && (
                <Link
                  to="/withdraw"
                  className="inline-flex items-center justify-center px-3 py-2 lg:px-4 lg:py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors text-sm lg:text-base"
                >
                  <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                  Withdraw Earnings
                </Link>
              )}
            </div>
          </div>
          
          <div className="hidden md:block">
            <div className="w-16 h-16 lg:w-20 lg:h-20 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="h-8 w-8 lg:h-10 lg:w-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Program Status */}
      {!currentUser?.role && (
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border-0 sm:border border-yellow-200 rounded-none sm:rounded-xl p-3 sm:p-4 lg:p-6 max-w-full">
          <div className="flex items-start">
            <div className="w-10 h-10 lg:w-12 lg:h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-3 lg:mr-4 flex-shrink-0">
              <AcademicCapIcon className="h-5 w-5 lg:h-6 lg:w-6 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-base lg:text-lg font-semibold text-yellow-800 mb-2">
                Complete Your Program Setup
              </h3>
              <p className="text-xs lg:text-sm text-yellow-700 mb-4">
                You haven't selected a professional program yet. Choose between ICAG or CITG to access 
                full course content and start your learning journey.
              </p>
              <Link
                to="/post-signup"
                className="inline-flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors text-sm lg:text-base"
              >
                Choose Program
                <ArrowRightIcon className="h-3 w-3 lg:h-4 lg:w-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* AI Chat Button */}
      <AIChatButton />

      {/* Course Preview Modal */}
      <CourseDetailsModal
        isOpen={showCourseModal}
        onClose={() => setShowCourseModal(false)}
        course={selectedCourse}
        onAddToCart={() => {}}
        onToggleFavorite={() => {}}
        isFavorite={false}
        isInCart={false}
      />

      {/* Edit Course Modal */}
      <EditCourseModal
        isOpen={showEditModal}
        onClose={handleEditModalClose}
        course={editingCourse}
      />
      </div>
    </div>
  );
};

export default StudentDashboard;
